import { DraftSaver } from '@shared/services/draft-saver';
import { Subscription } from 'rxjs';
import { HospitalServiceLocationService } from 'app/services/license/hospital/hospital-service-location.service';
import { trigger, transition, style, animate } from "@angular/animations";
import { Component, inject, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { SharedService } from "@shared/services/shared.service";
import { AddressComponent } from "app/components/reusable/address/address.component";
import { DocumentComponent } from "app/components/reusable/document/document.component";
import { IStatus } from "app/models/licenses/status";
import { IOnCampusBuilding } from "app/models/on-campus-building/on-campus-building";
import { AddressService } from "app/services/address/address.service";
import { DocumentService } from "app/services/document/document.service";
import { OfficeHoursService } from "app/services/license/office-hours.service";
import { OnCampusBuildingService } from "app/services/on-campus-building/on-campus-building.service";
import { HospitalServiceLocationComponent } from '../hospital-service-location/hospital-service-location.component';
import { firstValueFrom } from "rxjs";

@Component({
  selector: 'on-campus-building',
  templateUrl: './on-campus-building.component.html',
  styleUrls: ['./on-campus-building.component.scss'],
    animations: [
      trigger('slideRight', [
        transition(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
        ])
      ])
    ]
})

export class OnCampusBuildingComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChild(HospitalServiceLocationComponent) serviceLocations?: HospitalServiceLocationComponent;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() isChange: boolean = false;
  @Input() canEdit: boolean = false;
  @Input() licenseStatuses: IStatus[] = [];
  @Input() HospitalLicenseId: string = "";

  public entity: string = 'OnCampusBuilding';
  public addingBuilding: boolean = false;
  public deletingBuilding: boolean = false;
  public managingBuilding: boolean = false;
  public isOutOfState: boolean = false;
  public newBuildingCreated: boolean = false;
  public includeArchived: boolean = false;
  public onCampusBuildingTypes: string[] = ['Other'];

  public onCampusBuilding: IOnCampusBuilding = {
    id: 0,
    buildingId: "",
    buildingName: "",
    parentId: 0,
    archived: false,
    addresses: [],
    documents: []
  }
  public buildings: IOnCampusBuilding[] = [];
  public buildingForm = new FormGroup({
    buildingName: new FormControl('', Validators.required)
  });
  public onCampusBuildings: IOnCampusBuilding[] = [];


  public onCampusBuildingColumns: string [] = ['buildingId', 'buildingName', 'archived'];
  public onCampusBuildingSource = new MatTableDataSource<IOnCampusBuilding>(this.onCampusBuildings);
  @ViewChild('onCampusBuildingPaginator', {static: false}) onCampusBuildingPaginator!: MatPaginator;
  @ViewChild('onCampusBuildingSort', {static: false}) onCampusBuildingSort!: MatSort;

  public sharedService = inject(SharedService);
  public buildingService = inject(OnCampusBuildingService);
  public addressService = inject(AddressService);
  public officeHoursService = inject(OfficeHoursService);
  public documentService = inject(DocumentService);

  public saver?: DraftSaver;
  public isCreating = false;
  public isClosing = false;
  private creating?: Promise<void>;
  private formChanges?: Subscription;
  private readonly locationService = inject(HospitalServiceLocationService);

  private startSaving(): void {
    this.saver?.dispose();
    this.formChanges?.unsubscribe();
    this.saver = new DraftSaver(async () => {
      const values = this.buildingForm.getRawValue();
      const record = { ...this.onCampusBuilding, buildingName: values.buildingName ?? '' };
      if (!record.id || !this.canEdit) throw new Error('Parent is not editable');
      if (!await firstValueFrom(this.buildingService.updateOnCampusBuilding(record))) throw new Error('Save failed');
      Object.assign(this.onCampusBuilding, record);
    }, () => this.sharedService.toastr.error('Could not save building. Your edits are still here.'));
    this.formChanges = this.buildingForm.valueChanges.subscribe(() => {
      if (this.managingBuilding && this.canEdit) this.saver?.changed();
    });
  }

  async savePending(): Promise<boolean> {
    await this.creating;
    if (!this.canEdit || !this.managingBuilding) return true;
    if (this.serviceLocations && !await this.serviceLocations.savePending()) return false;
    return this.saver ? this.saver.flush() : true;
  }

  async closeWithSave(): Promise<void> {
    if (this.isClosing) return;
    this.isClosing = true;
    try {
      if (await this.savePending()) {
        this.saver?.dispose();
        this.formChanges?.unsubscribe();
        this.toggleBuildingForm(false);
      }
    } finally { this.isClosing = false; }
  }

  async validateServiceLocations(): Promise<string[]> {
    const errors: string[] = [];
    for (const parent of this.buildings.filter(item => !item.archived)) {
      if (!parent.buildingName.trim()) {
        errors.push('On-campus building has incomplete required details.');
      }
      const locations = await firstValueFrom(this.locationService.getHospitalServiceLocations(parent.id, 'OnCampusBuilding', false));
      for (const location of locations) {
        if (location.isDraft) errors.push(
          (parent.buildingName || 'Unnamed building') + ': service location ' +
          (location.floor || 'unnamed') + ' / ' + (location.suite || 'unnamed') + ' is incomplete. Complete its details and all days of office hours, or delete the draft.');
      }
    }
    return errors;
  }

  ngOnInit(): void {
    this.getBuildings(this.includeArchived);
    if(this.licenseStatuses.some(s => s.label === this.sharedService.started))
      this.onCampusBuildingColumns = ['buildingName', 'archived'];
    else
        this.onCampusBuildingColumns = ['buildingId', 'buildingName', 'archived'];
  }

  ngOnDestroy(): void {
    this.saver?.dispose();
    this.formChanges?.unsubscribe();
  }

  getBuildings(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.buildingService.getBuildings(this.entityId, this.entityType, this.includeArchived).subscribe({
      next: response => this.buildings = response,
      complete: () => this.updateBuildingTable()
    });
  }

  updateBuildingTable(): void {
    setTimeout(() => {
      this.onCampusBuildingSource.data = this.buildings;
      this.onCampusBuildingSource.paginator = this.onCampusBuildingPaginator;
      this.onCampusBuildingSource.sort = this.onCampusBuildingSort;
    }, 100);
  }

  selectBuilding(building: IOnCampusBuilding): void {
    this.deletingBuilding = false;
    this.onCampusBuilding = building;
    this.managingBuilding = true;
    this.toggleBuildingForm(true);
    this.updateBuildingForm(building);
    this.startSaving();
  }

  toggleBuildingForm(adding: boolean): void{
    this.managingBuilding = adding;
    this.updateBuildingForm(this.getEmptyBuilding());
    if(!adding){
      this.updateBuildingTable();
    }
  }

   getEmptyBuilding(): IOnCampusBuilding {
    return {
      id: 0,
      buildingId: "",
      buildingName: "",
      parentId: 0,
      archived: false,
      addresses: [],
      documents: []
    }
   }

   updateBuildingForm(building: IOnCampusBuilding): void {
     this.buildingForm.reset({
       buildingName: building.buildingName
     });
   }

   updateBuilding(): void {
     let form = this.buildingForm.value;
     this.onCampusBuilding.buildingName = form.buildingName ?? '';
   }

   async createBuilding(): Promise<void> {
    if (!this.canEdit || this.isCreating) return;
    this.isCreating = true;
    this.creating = (async () => {
      try {
        const record = { ...this.getEmptyBuilding(), parentId: this.entityId };
        const response = await firstValueFrom(this.buildingService.createBuilding(record, this.entityId, this.entityType));
        if (!response.id) throw new Error('Creation failed');
        this.buildings.push(response);
        this.onCampusBuilding = response;
        this.toggleBuildingForm(true);
        this.newBuildingCreated = true;
        this.deletingBuilding = false;
        this.startSaving();
      } catch {
        this.sharedService.toastr.error('Could not create building. Please try again.');
      } finally { this.isCreating = false; }
    })();
    await this.creating;
    this.creating = undefined;
   }

   async saveBuilding(): Promise<void> { await this.closeWithSave(); }

   confirmDelete(buildingId: number): void {
     this.sharedService.openConfirm('Delete on campus building ' + this.onCampusBuilding.buildingName + ' ?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.deletingBuilding = true;
           this.deleteBuilding(buildingId);
         }
       }
     );
   }

  async deleteBuilding(id: number): Promise<void> {
    if (this.isClosing) return;
    this.isClosing = true;
    try {
      await this.saver?.stop();
      await this.serviceLocations?.stopSaving();
      const deleted = await firstValueFrom(this.buildingService.deleteOnCampusBuilding(id));
      if (!deleted) throw new Error('Delete failed');
      this.saver = undefined;
      this.formChanges?.unsubscribe();
      this.newBuildingCreated = false;
      this.buildings = this.buildings.filter(item => item.id !== id);
      this.toggleBuildingForm(false);
    } catch {
      this.startSaving();
      this.saver?.changed();
      this.serviceLocations?.resumeSaving();
      this.sharedService.toastr.error('Could not delete location. Your edits are still here.');
    } finally { this.isClosing = false; }
  }

  async validateBuildingAddresses(): Promise<string[]> {
    let errors: string[] = [];

    for (const building of this.buildings) {
      try {
        const response = await firstValueFrom(
          this.addressService.getAddresses(building.id, 'OnCampusBuilding', false)
        );
        building.addresses = response;

        if (!building.addresses.find(a =>
          !a.archived && a.addressTypes.find(t => t.type === 'Physical')
        )) {
          errors.push(`Building ${building.buildingName} must have a physical address`);
        }
      } catch (e) {
        console.log('Get address error', e);
      }
    }

    return errors;
  }
}
